/**
 * Webhooks Public API Client - CORS-compatible client for webhook data
 * 
 * Uses the dedicated webhooks-public Edge Function to avoid CORS issues
 * that occur with the admin function requiring authentication
 */

const WEBHOOK_ENDPOINTS = new Set([
  'webhooks.recent',
  'webhooks.security.summary', 
  'webhooks.stats'
]);

export function isWebhookEndpoint(act: string): boolean {
  return WEBHOOK_ENDPOINTS.has(act);
}

interface WebhookApiResult<T = unknown> {
  ok: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export class WebhooksPublicClient {
  private readonly baseUrl: string;
  private readonly defaultTimeoutMs: number;

  constructor() {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL environment variable is required');
    }
    this.baseUrl = `${supabaseUrl}/functions/v1/webhooks-public`;
    this.defaultTimeoutMs = 8000;
  }

  async call<T = unknown>(
    endpoint: string,
    params: Record<string, string> = {}
  ): Promise<WebhookApiResult<T>> {
    if (!WEBHOOK_ENDPOINTS.has(endpoint)) {
      return {
        ok: false,
        error: { code: 'UNSUPPORTED_ENDPOINT', message: `Endpoint '${endpoint}' not supported by webhooks client` }
      };
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.defaultTimeoutMs);

    try {
      // Get user session for authenticated request
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      const url = new URL(this.baseUrl);
      url.searchParams.set('endpoint', endpoint);

      // Add any additional parameters
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });

      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const headers: Record<string, string> = {
        'Accept': 'application/json',
      };

      // Add required Supabase headers for authentication
      if (anonKey) {
        headers['apikey'] = anonKey;
      }

      // Use user access token if available, fallback to anon key
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      } else if (anonKey) {
        headers['Authorization'] = `Bearer ${anonKey}`;
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers,
        signal: controller.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}`;
        
        try {
          const errorBody = JSON.parse(errorText);
          errorMessage = errorBody.error?.message || errorBody.message || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }

        return {
          ok: false,
          error: { code: 'HTTP_ERROR', message: errorMessage }
        };
      }

      const data = await response.json();
      return {
        ok: true,
        data: data as T
      };

    } catch (error: any) {
      const isAborted = error.name === 'AbortError';
      return {
        ok: false,
        error: {
          code: isAborted ? 'TIMEOUT' : 'NETWORK_ERROR',
          message: isAborted ? 'Request timed out' : (error.message || 'Network request failed')
        }
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

// Single instance for app-wide use
export const webhooksPublicClient = new WebhooksPublicClient();